import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import dashboardService from '@services/dashboardService';
import { DashboardSummary } from '@/types/dashboard';
import { RevenueCapPanel } from './panels/RevenueCapPanel';
import { CardDivergencesPanel } from './panels/CardDivergencesPanel';
import { DashboardCalendarPanel } from './panels/DashboardCalendarPanel';
import { CertificatesPanel } from './panels/CertificatesPanel';
import { RecentMaterialReceiptsPanel } from './panels/RecentMaterialReceiptsPanel';
import { PaymentsTodayPanel } from './panels/PaymentsTodayPanel';
import { DemoUnpaidCustomersPanel } from './panels/DemoUnpaidCustomersPanel';

/**
 * Dashboard principal por perfil.
 *
 * Cada painel é habilitado pelo backend, em `panels`, conforme o perfil do
 * usuário — o frontend só renderiza o que vier.
 *
 * As seções "Ações Rápidas" e "Alertas do Sistema" foram removidas. A primeira
 * duplicava o menu; a segunda mostrava, na ausência de avisos, um card verde
 * afirmando que "todos os sistemas estão funcionando normalmente" sem checar
 * sistema nenhum — e o único aviso que ela chegava a exibir (certificado
 * bancário vencendo) passou a ser coberto, com mais contexto, pelo painel de
 * Certificados Digitais.
 */
export const DashboardView: React.FC = () => {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);

  const loadSummary = useCallback(async () => {
    try {
      const data = await dashboardService.getSummary();
      setSummary(data);
    } catch (error) {
      console.error('Erro ao carregar resumo do dashboard:', error);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-h1 mb-2">Dashboard</h1>
        <p className="text-slate-600">
          Visão geral do sistema ERP - {new Date().toLocaleDateString('pt-BR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      </div>

      {/* Calendário de prioridades: compras/vendas prioritárias, previsões de
          entrega de fornecedores e envios agendados (todos os perfis). */}
      {summary?.panels.includes('calendar') && (
        <section>
          <DashboardCalendarPanel />
        </section>
      )}

      {/* Faturamento do mês por empresa (perfil com painel revenue_caps) */}
      {summary?.panels.includes('revenue_caps') && summary.revenue_caps && (
        <section>
          <h2 className="text-h2 mb-6">Faturamento do mês por empresa</h2>
          <RevenueCapPanel data={summary.revenue_caps} />
        </section>
      )}

      {/* Divergências da conciliação Cielo (admin/finance): pagou a menos ou
          parcela ausente do arquivo. Só aparece quando há o que tratar. */}
      {summary?.panels.includes('card_divergences') && summary.card_divergences && (
        <section>
          <h2 className="text-h2 mb-6">Divergências de Recebíveis Cielo</h2>
          <CardDivergencesPanel data={summary.card_divergences} onChanged={loadSummary} />
        </section>
      )}

      {/* Pagamentos previstos para hoje, por empresa e conta (admin/finance). */}
      {summary?.panels.includes('payments_today') && summary.payments_today && (
        <section>
          <h2 className="text-h2 mb-6">Pagamentos previstos hoje</h2>
          <PaymentsTodayPanel data={summary.payments_today} />
        </section>
      )}

      {/* Clientes com pedidos de demonstração ainda não conciliados (admin/finance). */}
      {summary?.panels.includes('demo_unpaid_customers') && summary.demo_unpaid_customers && (
        <section>
          <h2 className="text-h2 mb-6">Clientes com demonstração sem pagamento</h2>
          <DemoUnpaidCustomersPanel data={summary.demo_unpaid_customers} />
        </section>
      )}

      {/* Recebimentos de materiais dos últimos 5 dias (admin/finance). */}
      {summary?.panels.includes('recent_material_receipts') && summary.recent_material_receipts && (
        <section>
          <h2 className="text-h2 mb-6">Recebimentos de materiais (últimos 5 dias)</h2>
          <RecentMaterialReceiptsPanel data={summary.recent_material_receipts} />
        </section>
      )}

      {/* Certificados digitais: validade dos certificados das integrações (PIX,
          extrato de conta corrente e o A1 que assina NF-e e e-Social), com
          chamada de renovação a partir de 30 dias do vencimento. O backend só
          envia o painel `certificates` para admin e finance — o PermissionGuard
          aqui é a segunda camada. */}
      {summary?.panels.includes('certificates') && summary.certificates && (
        <PermissionGuard permission="certificates.monitor.read">
          <section>
            <h2 className="text-h2 mb-6">Certificados Digitais</h2>
            <CertificatesPanel data={summary.certificates} />
          </section>
        </PermissionGuard>
      )}

      {/* Informações do sistema */}
      <section>
        <Card className="bg-slate-100 border-slate-200">
          <CardContent>
            <div className="text-center py-8">
              <h3 className="text-h3 text-slate-700 mb-2">
                Sistema ERP Viver Organizer
              </h3>
              <p className="text-slate-500">
                Versão 2025.1.0
              </p>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};
